import { useState, useEffect } from 'react';
import { Modal, Stack, Text, Button, Group, Alert, UnstyledButton } from '@mantine/core';
import { IconMapPin, IconAlertCircle } from '@tabler/icons-react';
import { useAuth } from '../../hooks/useAuth';
import { selectKrogerLocation, getSelectedKrogerLocation } from '../../utils/krogerLocations';
import { KrogerLocationPicker } from '../Onboarding/KrogerLocationPicker';
import { KrogerLocation } from '../../types/kroger';

type KrogerLocationModalProps = {
  opened: boolean;
  onClose: () => void;
  onLocationSelected?: (locationId: string, fulfillmentPreference: 'pickup' | 'delivery') => void;
  title?: string;
  description?: string;
};

export const KrogerLocationModal = ({
  opened,
  onClose,
  onLocationSelected,
  title = "Select your Kroger store",
  description = "Choose your preferred Kroger location to enable grocery shopping features."
}: KrogerLocationModalProps) => {
  const { user } = useAuth();
  const [selectedLocationId, setSelectedLocationId] = useState<string | undefined>(undefined);
  const [selectedFulfillmentPreference, setSelectedFulfillmentPreference] = useState<'pickup' | 'delivery'>('pickup');
  const [isSelectingLocation, setIsSelectingLocation] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load existing location when modal opens
  useEffect(() => {
    const loadExistingLocation = async () => {
      if (opened && user && !selectedLocationId) {
        try {
          const existingLocationId = await getSelectedKrogerLocation(user);
          if (existingLocationId) {
            setSelectedLocationId(existingLocationId);
          }
        } catch (error) {
          console.error('Error loading existing location:', error);
        }
      }
    };

    loadExistingLocation();
  }, [opened, user, selectedLocationId]);

  const handleLocationSelect = (location: KrogerLocation, fulfillmentPreference: 'pickup' | 'delivery') => {
    // Just update local state - don't save until "Done" is clicked
    setSelectedLocationId(location.locationId);
    setSelectedFulfillmentPreference(fulfillmentPreference);
    setError(null);
  };

  const handleSaveLocation = async () => {
    if (!user || !selectedLocationId) return;

    setIsSelectingLocation(true);
    setError(null);

    try {
      await selectKrogerLocation(user, selectedLocationId, selectedFulfillmentPreference);
      
      // Notify parent component
      onLocationSelected?.(selectedLocationId, selectedFulfillmentPreference);
      
      // Close modal
      onClose();
    } catch (error) {
      console.error('Error selecting Kroger location:', error);
      setError('Failed to save your store selection. Please try again.');
    } finally {
      setIsSelectingLocation(false);
    }
  };

  const handleClose = () => {
    setError(null);
    onClose();
  };

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={
        <Group gap="sm">
          <IconMapPin size={20} />
          <Text fw={600} size="lg">{title}</Text>
        </Group>
      }
      size="lg"
      centered
      radius="lg"
    >
      <Stack gap="lg">
        {description && (
          <Text size="sm" c="dimmed">
            {description}
          </Text>
        )}

        {error && (
          <Alert icon={<IconAlertCircle size={16} />} color="red" radius="lg">
            {error}
          </Alert>
        )}

        <KrogerLocationPicker
          onLocationSelect={handleLocationSelect}
          selectedLocationId={selectedLocationId}
          selectedFulfillmentPreference={selectedFulfillmentPreference}
        />

        {selectedLocationId && (
          <Group justify="flex-end" pt="md" gap="lg">
            <UnstyledButton onClick={handleClose}>
              <Text size="sm" c="dimmed">Cancel</Text>
            </UnstyledButton>
            <Button
              onClick={handleSaveLocation}
              loading={isSelectingLocation}
              disabled={isSelectingLocation}
            >
              Done
            </Button>
          </Group>
        )}
      </Stack>
    </Modal>
  );
};
